/**
 * Outcome of a session refresh attempt. Callers use it to separate a truly dead session
 * from a transient outage, so a Keycloak hiccup no longer logs the user out:
 * - `refreshed`: fresh cookies were set (204).
 * - `expired`: the refresh token is definitively dead (401) — sign the user out.
 * - `transient`: unreachable or 5xx — the session is intact, surface a retryable error
 *   instead of logging out.
 *
 * Collapsing this to a boolean is what makes an app bounce users to the login page on a
 * Keycloak blip. Keep the three states all the way to the call site.
 */
export type RefreshOutcome = "refreshed" | "expired" | "transient";

export const DEFAULT_REFRESH_ENDPOINT = "/api/auth/refresh";

/**
 * One in-flight slot PER endpoint. A single shared slot would hand a caller the outcome of a
 * rotation performed against a different authority: it would read `refreshed` while its own
 * cookies were never rotated, replay with a stale bearer and loop on 401.
 */
const inFlight = new Map<string, Promise<RefreshOutcome>>();

/**
 * Rotates the browser session through the authority's refresh endpoint, which mints fresh
 * cookies from the httpOnly refresh token.
 *
 * Concurrent callers share a single in-flight request: a page renders 40-70 authenticated
 * calls, so without coalescing a burst of 401s would fire that many competing rotations
 * against a single-use refresh token and log the user out on the losers. Every refresh path
 * in an app must go through this function — a raw `fetch` to the endpoint races it and
 * burns the token.
 *
 * `credentials: 'include'` is required for a cross-origin (but same-site) call to the
 * authority host, and harmless same-origin.
 */
export function refreshSession(
  endpoint: string = DEFAULT_REFRESH_ENDPOINT,
): Promise<RefreshOutcome> {
  const pending = inFlight.get(endpoint);
  if (pending) {
    return pending;
  }

  const attempt = (async () => {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        return "refreshed";
      }
      return response.status === 401 ? "expired" : "transient";
    } catch {
      return "transient";
    }
  })();

  // Release the slot only once the attempt has settled, and from OUTSIDE the async body: a
  // `finally` inside it runs synchronously when `fetch` throws synchronously, i.e. before the
  // `set` below, which would strand a settled promise in the map forever.
  inFlight.set(endpoint, attempt);
  void attempt.finally(() => inFlight.delete(endpoint));
  return attempt;
}
